import { Box, Button, Skeleton, styled, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFetchData } from '../../../../hooks/useFetchData';
import { selectAppStatus } from '../../../../redux/reducers/appStatusReducer';
import { Conversation } from '../../../../Types/conversation';
import { Project, ProjectRun, ProjectSalesforceInfo } from '../../../../Types/dealGPT';
import { CardSize, ConversationType, PaginationMode, RunType } from '../../../../Types/enums';
import { Folder } from '../../../../Types/folder';
import { conversationsPathUrl, getLatestProjectRunPathUrl } from '../../../../util/routesUrls';
import Card from '../../../Card/Card';

interface ResearchPromptsProps {
  folderId: Folder['id'] | undefined;
  projectId: Project['id'] | undefined;
  salesforce: ProjectSalesforceInfo | null;
  onConversationClick: (conversation: Conversation) => void;
}

const LoadMoreButton = styled(Button)({
  margin: '4px 0px 8px 0px',
  color: 'black',
  fontWeight: 'bold',
});

const ResearchPrompts = ({
  folderId,
  projectId,
  salesforce,
  onConversationClick,
}: ResearchPromptsProps) => {
  const { selectedApp } = useSelector(selectAppStatus);

  const { data: promptsRun, loading: loadingRun } = useFetchData<ProjectRun>({
    urlPath: getLatestProjectRunPathUrl(projectId || '', RunType.PROMPT_KICKOFF),
    skip: !projectId,
  });

  const {
    data: conversations,
    pagination,
    nextPage,
    loading: loadingConversations,
  } = useFetchData<Conversation>({
    urlPath: conversationsPathUrl(
      selectedApp,
      undefined,
      undefined,
      folderId,
      undefined,
      undefined,
      ConversationType.AUTOSTART_PROMPT
    ),
    paginated: true,
    paginationMode: PaginationMode.APPEND,
  });

  const showMoreButton = pagination?.hasNextPage || false;
  const hasSalesforceInfo = salesforce && salesforce.industry && salesforce.subIndustry;

  const description = useMemo(() => {
    if (!hasSalesforceInfo) {
      return 'Industry insights can’t be generated because the company’s industry and sub-industry are missing in CRM or no opportunity is currently linked to this project.';
    }

    if (promptsRun && !promptsRun.endedAt) {
      return (
        promptsRun?.details ||
        'We’re pulling together industry insights to help you prepare. Check back shortly.'
      );
    }

    return 'These insights provide an overview of the company’s industry and sub-industry, helping consultants quickly build market context.';
  }, [hasSalesforceInfo, promptsRun]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">
        {hasSalesforceInfo ? 'Industry Research Brief' : 'No industry found'}
      </Typography>
      <Typography variant="body2">{description}</Typography>
      {hasSalesforceInfo &&
        (!promptsRun || (promptsRun?.endedAt && conversations?.length === 0)) && (
          <Box display="flex" width="100%" justifyContent="center" m={2}>
            <Typography variant="body2">
              {promptsRun
                ? 'No industry research found.'
                : 'No industry research has been run. Please sync and update files to start the process.'}
            </Typography>
          </Box>
        )}
      {loadingConversations || loadingRun
        ? Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={`skeleton_${index}`} variant="rectangular" width="100%" height={95} />
          ))
        : hasSalesforceInfo &&
          promptsRun?.endedAt && (
            <>
              {(conversations || []).map((conversation) => (
                <Card
                  key={conversation.id}
                  type="conversation"
                  title={conversation.title}
                  searchQuery=""
                  selected={false}
                  pinned={false}
                  matchingMessage={conversation.matchingMessage}
                  CardContextMenu={<></>}
                  onCardClick={() => onConversationClick(conversation)}
                  time={conversation.lastActivityAt}
                  folderName={conversation.folderName ?? ''}
                  tools={conversation.toolsUsed}
                  size={CardSize.FULL}
                />
              ))}
              {showMoreButton && (
                <LoadMoreButton onClick={nextPage}>
                  <Typography variant="caption2">+ Load More</Typography>
                </LoadMoreButton>
              )}
            </>
          )}
    </Box>
  );
};

export default ResearchPrompts;
